import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, Timestamp, doc, setDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { db } from "../../firebase/config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function HyperadminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // États pour créer une entreprise
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [industry, setIndustry] = useState("");
  const [subscription, setSubscription] = useState("standard");
  
  // États pour créer le premier admin de l'entreprise
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  
  // Timeline par défaut
  const [timelineAffaire, setTimelineAffaire] = useState(["Visite", "Proposition", "Signature"]);
  const [timelineProjet, setTimelineProjet] = useState(["Étude", "Installation", "Suivi"]);
  
  // Liste des entreprises
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Vérifier que l'utilisateur est bien hyperadmin
  useEffect(() => {
    if (!user || user.email !== "contact@energesync.com") {
      alert("Accès refusé - Hyperadmin uniquement");
      navigate("/login");
    }
  }, [user, navigate]);

  const handleAddStep = (setter, current) => {
    setter([...current, ""]);
  };

  const handleChangeStep = (index, value, setter, current) => {
    const updated = [...current];
    updated[index] = value;
    setter(updated);
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$";
    const password = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setAdminPassword(password);
  };

  const handleCreateCompany = async () => {
    if (!companyName || !companyAddress || !adminName || !adminEmail || !adminPassword) {
      return alert("Tous les champs sont requis");
    }

    setLoading(true);
    
    try {
      // 1. Créer le compte admin Firebase Auth
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
      const adminUid = userCredential.user.uid;

      // 2. Créer l'entreprise dans Firestore
      const companyRef = await addDoc(collection(db, "companies"), {
        name: companyName,
        address: companyAddress,
        industry,
        subscription,
        superAdminId: adminUid,
        superAdminEmail: adminEmail,
        superAdminName: adminName,
        timelines: {
          chargeAffaire: timelineAffaire,
          chefProjet: timelineProjet
        },
        createdAt: Timestamp.now(),
        createdBy: user.uid,
        status: "active",
        settings: {
          maxUsers: subscription === "premium" ? 50 : 10,
          features: subscription === "premium" ? ["all"] : ["basic"]
        }
      });

      // 3. Créer le profil du super admin
      await setDoc(doc(db, "users", adminUid), {
        email: adminEmail,
        displayName: adminName,
        nom: adminName,
        phone: adminPhone,
        role: "superadmin",
        companyId: companyRef.id,
        companyName: companyName,
        createdAt: Timestamp.now(),
        isCompanyOwner: true
      });

      // 4. Mettre à jour la liste des entreprises
      setCompanies(prev => [...prev, { 
        id: companyRef.id, 
        name: companyName,
        superAdminName: adminName,
        superAdminEmail: adminEmail,
        subscription,
        status: "active",
        createdAt: new Date()
      }]);

      // 5. Réinitialiser le formulaire
      setCompanyName("");
      setCompanyAddress("");
      setIndustry("");
      setSubscription("standard");
      setAdminName("");
      setAdminEmail("");
      setAdminPhone("");
      setAdminPassword("");
      setTimelineAffaire(["Visite", "Proposition", "Signature"]);
      setTimelineProjet(["Étude", "Installation", "Suivi"]);

      alert(`✅ Entreprise créée avec succès !
      
🏢 Entreprise: ${companyName}
👤 Admin: ${adminName}
📧 Email: ${adminEmail}
🔑 Mot de passe: ${adminPassword}

L'admin peut maintenant se connecter sur l'application.`);

    } catch (error) {
      console.error("Erreur création entreprise:", error);
      if (error.code === 'auth/email-already-in-use') {
        alert("❌ Cette adresse email est déjà utilisée");
      } else {
        alert("❌ Erreur lors de la création: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger les entreprises existantes
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "companies"));
        const list = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        setCompanies(list);
      } catch (error) {
        console.error("Erreur chargement entreprises:", error);
      }
    };
    fetchCompanies();
  }, []);

  if (!user || user.email !== "contact@energesync.com") {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Accès Refusé</h1>
        <p>Seul l'hyperadmin peut accéder à cette section.</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">🏢 HyperAdmin Panel</h1>
        <p className="text-gray-600">Gestion des entreprises clientes - {user.email}</p>
      </div>

      {/* Formulaire de création d'entreprise */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">🆕 Créer une nouvelle entreprise</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations entreprise */}
          <div>
            <h3 className="font-semibold mb-3 text-blue-600">📋 Informations Entreprise</h3>
            <input 
              type="text" 
              placeholder="Nom de l'entreprise *" 
              value={companyName} 
              onChange={(e) => setCompanyName(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <input 
              type="text" 
              placeholder="Adresse complète *" 
              value={companyAddress} 
              onChange={(e) => setCompanyAddress(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <input 
              type="text" 
              placeholder="Secteur d'activité" 
              value={industry} 
              onChange={(e) => setIndustry(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <select 
              value={subscription} 
              onChange={(e) => setSubscription(e.target.value)} 
              className="w-full border p-3 rounded mb-3"
            >
              <option value="standard">Standard (10 utilisateurs)</option>
              <option value="premium">Premium (50 utilisateurs)</option>
            </select>
          </div>

          {/* Informations admin */}
          <div>
            <h3 className="font-semibold mb-3 text-green-600">👤 Premier Administrateur</h3>
            <input 
              type="text" 
              placeholder="Nom complet *" 
              value={adminName} 
              onChange={(e) => setAdminName(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <input 
              type="email" 
              placeholder="Email *" 
              value={adminEmail} 
              onChange={(e) => setAdminEmail(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <input 
              type="text" 
              placeholder="Téléphone" 
              value={adminPhone} 
              onChange={(e) => setAdminPhone(e.target.value)} 
              className="w-full border p-3 rounded mb-3" 
            />
            <div className="flex gap-2 mb-3">
              <input 
                type="text" 
                placeholder="Mot de passe *" 
                value={adminPassword} 
                onChange={(e) => setAdminPassword(e.target.value)} 
                className="flex-1 border p-3 rounded" 
              />
              <button 
                onClick={generatePassword}
                className="bg-blue-500 text-white px-4 py-3 rounded hover:bg-blue-600"
              >
                🎲 Générer
              </button>
            </div>
          </div>
        </div>

        {/* Timeline personnalisée */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <h3 className="font-semibold mb-3">📈 Timeline - Chargé d'Affaires</h3>
            {timelineAffaire.map((step, index) => (
              <input 
                key={`affaire-${index}`} 
                type="text" 
                value={step} 
                onChange={(e) => handleChangeStep(index, e.target.value, setTimelineAffaire, timelineAffaire)} 
                className="w-full border p-2 rounded mb-2" 
              />
            ))}
            <button 
              onClick={() => handleAddStep(setTimelineAffaire, timelineAffaire)} 
              className="text-sm text-blue-500 hover:text-blue-700"
            >
              + Ajouter une étape
            </button>
          </div>

          <div>
            <h3 className="font-semibold mb-3">🔧 Timeline - Chef de Projet</h3>
            {timelineProjet.map((step, index) => (
              <input 
                key={`projet-${index}`} 
                type="text" 
                value={step} 
                onChange={(e) => handleChangeStep(index, e.target.value, setTimelineProjet, timelineProjet)} 
                className="w-full border p-2 rounded mb-2" 
              />
            ))}
            <button 
              onClick={() => handleAddStep(setTimelineProjet, timelineProjet)} 
              className="text-sm text-blue-500 hover:text-blue-700"
            >
              + Ajouter une étape
            </button>
          </div>
        </div>

        <button 
          onClick={handleCreateCompany} 
          disabled={loading}
          className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all mt-6 disabled:opacity-50"
        >
          {loading ? "🔄 Création en cours..." : "🚀 Créer l'entreprise"}
        </button>
      </div>

      {/* Liste des entreprises */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">🏢 Entreprises Clientes ({companies.length})</h2>
        
        {companies.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucune entreprise créée pour le moment</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border">Entreprise</th>
                  <th className="p-3 border">Administrateur</th>
                  <th className="p-3 border">Email</th>
                  <th className="p-3 border">Abonnement</th>
                  <th className="p-3 border">Statut</th>
                  <th className="p-3 border">Créée le</th>
                  <th className="p-3 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(company => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="p-3 border font-medium">{company.name}</td>
                    <td className="p-3 border">{company.superAdminName}</td>
                    <td className="p-3 border">{company.superAdminEmail}</td>
                    <td className="p-3 border">
                      <span className={`px-2 py-1 rounded text-xs ${
                        company.subscription === 'premium' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {company.subscription}
                      </span>
                    </td>
                    <td className="p-3 border">
                      <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                        {company.status}
                      </span>
                    </td>
                    <td className="p-3 border text-sm">
                      {company.createdAt?.toLocaleDateString()}
                    </td>
                    <td className="p-3 border">
                      <button 
                        onClick={() => navigator.clipboard.writeText(`Email: ${company.superAdminEmail}`)}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        📋 Copier info
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}